/**
 * [화면 맞춤] 창(2026-10-20) — 올린 HTML 을 적용할 화면에 맞춰 보고 조정한다(`HtmlScreenFit`).
 *
 * 올린 직후에 저절로 뜬다 — 상세로 들어가야 맞출 수 있으면 올린 사람은 그대로 편성에 넣고, 화면에서 반만 보이는 것을 그때
 * 안다. 여러 개를 함께 올렸으면 하나씩 넘겨 본다. 목록의 [화면 맞춤] 단추도 이 창을 연다.
 */
import { useCallback, useState, type ReactNode } from 'react';
import type { SignageOneClient } from '../api/client.js';
import { useAsync } from '../hooks.js';
import { useGuardedClose } from './closeGuard.js';
import { HtmlScreenFit } from './HtmlScreenFit.js';
import { ErrorNote, Modal } from './ui.js';

export function HtmlFitModal({
  client,
  assetIds,
  canWrite,
  onClose,
  onChanged,
}: {
  client: SignageOneClient;
  /** 맞춰 볼 올린 HTML — 둘 이상이면 차례로 넘긴다. */
  assetIds: readonly string[];
  canWrite: boolean;
  onClose: () => void;
  /** 저장·재기로 콘텐츠가 바뀌었다 — 목록을 새로 읽는다. */
  onChanged: () => void;
}): ReactNode {
  const [index, setIndex] = useState(0);
  const id = assetIds[Math.min(index, assetIds.length - 1)]!;
  const asset = useAsync(() => client.getAsset(id), [client, id]);
  const [dirty, setDirty] = useState(false);
  const close = useGuardedClose(dirty, onClose);
  const { reload } = asset;
  const changed = useCallback(() => {
    reload();
    onChanged();
  }, [reload, onChanged]);
  // 다음 것으로 넘길 때도 저장하지 않은 조정을 버리기 전에 묻는다 — 닫기와 같은 물음이다.
  const move = useGuardedClose(dirty, () => {
    setDirty(false);
    setIndex((value) => (value + 1) % assetIds.length);
  });

  const title = asset.data ? `화면에 맞추기 — ${asset.data.title}` : '화면에 맞추기';
  return (
    <Modal title={title} onClose={close} wide>
      <div className="space-y-3">
        {assetIds.length > 1 ? (
          <p className="text-[12.5px] text-slate-600">
            올린 HTML {assetIds.length}개 가운데 {index + 1}번째 — 적용할 화면을 고르고 그 화면에서 어떻게 나가는지 보세요.
          </p>
        ) : (
          <p className="text-[12.5px] text-slate-600">적용할 화면을 고르면 그 화면 크기로 미리 보입니다. 맞지 않으면 아래에서 조정하고 저장하세요.</p>
        )}
        {asset.error ? <ErrorNote message={asset.error} /> : null}
        {asset.data && asset.data.id === id ? (
          <HtmlScreenFit key={id} client={client} asset={asset.data} canWrite={canWrite} onChanged={changed} onDirtyChange={setDirty} />
        ) : (
          <p className="hint">불러오는 중…</p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {assetIds.length > 1 ? (
            <button type="button" className="btn btn-sm" onClick={move}>
              다음 HTML ({((index + 1) % assetIds.length) + 1}/{assetIds.length})
            </button>
          ) : null}
          <button type="button" className="btn btn-sm" onClick={close}>
            닫기
          </button>
        </div>
      </div>
    </Modal>
  );
}
